import { apiClient, signApiRequest } from ".";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { CustomerCredentials } from "../ui/customerDataStore";
import { AxiosError } from "axios";
import { captureException } from "@sentry/react";
import { TRANSFER_STATUSES } from "./useGetCalculationById";

// Funds are stored as a JSON string on the advice and parsed by the caller.
export interface AdviceFund {
  isin: string;
  name: string;
  shareOfTotal: number;
}

export interface TInsuranceAdvice {
  enablePaymentProtection: boolean;
  insuranceCompany: string;
  insuranceIdentifier: string;
  paymentPeriod: number;
  toFunds: unknown;
  fromFunds: unknown;
  paymentTerm: unknown;
  transferDocument: string;
  statusInternal: TRANSFER_STATUSES;
}

interface TGetAdviceResponse {
  hasAgreedAdvice: boolean;
  adviceNumber: number;
  insuranceAdvices: TInsuranceAdvice[];
}

export const getAdviceById = async ({
  credentials,
}: {
  credentials: CustomerCredentials;
}) => {
  const signedRequest = await signApiRequest(
    "GET",
    `${import.meta.env.VITE_API_URL}/advice/get-advice?fid=${credentials.identityId}`,
    credentials,
  );

  const response = await apiClient(signedRequest);
  return response.data;
};

export const useGetAdviceById = ({
  credentials,
  enabled = true,
}: {
  credentials: CustomerCredentials;
  enabled?: boolean;
}) => {
  const fetchPOATextQuery = useQuery<TGetAdviceResponse, AxiosError>({
    queryKey: ["get-advice-by-id"],
    queryFn: () => getAdviceById({ credentials }),
    enabled,
    throwOnError: (error) => {
      captureException(error, {
        tags: {
          operation: "get-advice-by-id",
          errorType: error.name || "UnknownError",
        },
        extra: {
          apiEndpoint: "/advice/get-advice?fid=${fid}",
        },
        level: "error",
      });
      return true;
    },
  });
  return fetchPOATextQuery;
};

export const getAdviceQueryOptions = (credentials: CustomerCredentials) =>
  queryOptions({
    queryKey: ["get-advice-by-id"],
    queryFn: () => getAdviceById({ credentials }),
    retry: false,
    staleTime: 30_000,
  });
